import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, message, Select, Spin } from 'antd';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { DeleteOutlined, ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';

import { ticketById as ticketByIdAction,
  ticketByIdClear as ticketByIdClearAction,
  TicketByIdGetParams } from '../../../../store/ticket/byId';
import { Action, useAuth } from '../../../../store/auth';
import { useLayoutContext } from '../../../Layout/context';
import { TicketStatus, TicketStatusTitle, useTicket } from '../../../../store/ticket';
import ContentHeader from '../../../Common/ContentHeader';
import { ticketUpdate as ticketUpdateAction,
  ticketUpdateClear as ticketUpdateClearAction,
  ticketUpdateErrorClear as ticketUpdateErrorClearAction } from '../../../../store/ticket/update';
import { TicketDelete,
  ticketDelete as ticketDeleteAction,
  ticketDeleteClear as ticketDeleteClearAction } from '../../../../store/ticket/delete';
import { useSimpleModal } from '../../../Common/Modal/Simple';

import BadgeTicketStatus from '../../../Common/BadgeTicketStatus';
import { displayDate } from '../../../../utils/date';

import styles from './index.module.scss';

interface TicketViewContentProps {
  ticketById: (payload: TicketByIdGetParams) => Action<TicketByIdGetParams>;
  ticketByIdClear: () => Action;
  ticketUpdate: typeof ticketUpdateAction;
  ticketUpdateClear: typeof ticketUpdateClearAction;
  ticketUpdateErrorClear: typeof ticketUpdateErrorClearAction;
  ticketDelete: (payload: TicketDelete) => Action<TicketDelete>;
  ticketDeleteClear: () => Action;
}

function TicketViewContent({
  ticketById,
  ticketByIdClear,
  ticketUpdate,
  ticketUpdateClear,
  ticketUpdateErrorClear,
  ticketDelete,
  ticketDeleteClear,
}: TicketViewContentProps): JSX.Element {
  const { modal, contextHolder } = useSimpleModal();
  const { user } = useAuth();
  const { ticketId } = useParams();
  const [status, setStatus] = useState<TicketStatus | null>(null);
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { setBreadcrumbs } = useLayoutContext();

  const { byId, update, delete: deleteTicket } = useTicket();

  const responseUser = useMemo(() => byId.response?.user, [byId.response]);

  useEffect(() => {
    setBreadcrumbs([{ title: 'Ticket List', href: '/tickets' }, { title: 'Ticket View' }]);
  }, []);

  useEffect(() => {
    if (byId.response && !byId.error) {
      setStatus(byId.response.status);
    }
  }, [byId.response]);

  useEffect(() => {
    if (ticketId) {
      ticketById({ id: ticketId });
    }
  }, [ticketId, update.response]);

  useEffect(() => {
    if (byId.response) {
      form.setFieldsValue({
        description: byId.response.description,
        status: byId.response.status,
        updatedAt: dayjs(byId.response.updatedAt),
      });
    }
  }, [byId.response]);

  useEffect(
    () => () => {
      ticketByIdClear();
      ticketUpdateClear();
      ticketUpdateErrorClear();
      ticketDeleteClear();
    },
    [],
  );

  const updateTicketStatus = (s: TicketStatus) => {
    if (ticketId) {
      ticketUpdate({ id: ticketId, body: { status: s } });
    }
  };

  const confirmApply = () => {
    if (status) {
      modal.confirm({
        title: 'Are you sure you want to change status the ticket?',
        icon: <ExclamationCircleOutlined />,
        onOk: () => updateTicketStatus(status),
        okText: TicketStatusTitle[status],
      });
    }
  };

  useEffect(() => {
    if (update.response) {
      message.success('Successfully updated').then(ticketUpdateClear);
    }
  }, [update.response]);

  useEffect(() => {
    if (update.error) {
      message.error(JSON.stringify(update.error.data.message)).then(ticketUpdateErrorClear);
    }
  }, [update.error]);

  const confirmDelete = () => {
    if (ticketId) {
      modal.confirm({
        title: 'Are you sure you want to Delete the ticket?',
        icon: <ExclamationCircleOutlined />,
        onOk: () => ticketDelete({ id: ticketId }),
        okText: 'Delete',
      });
    }
  };

  useEffect(() => {
    if (deleteTicket.response) {
      navigate('/tickets');
    }
  }, [deleteTicket.response]);

  useEffect(() => {
    if (deleteTicket.error) {
      message.error(JSON.stringify(deleteTicket.error.data.message));
    }
  }, [deleteTicket.error]);

  return (
    <>
      <ContentHeader title={`Ticket ${byId.response?.ticketId}`}>
        {user?.role === 'root' && (
          <div className={styles.actions}>
            <Select
              value={status}
              style={{ width: 120 }}
              onChange={(value) => setStatus(value)}
              loading={update.loading}
              options={[
                {
                  value: TicketStatus.OPEN,
                  label: TicketStatusTitle.open,
                },
                {
                  value: TicketStatus.RESOLVED,
                  label: TicketStatusTitle.resolved,
                },
                {
                  value: TicketStatus.IN_PROGRESS,
                  label: TicketStatusTitle['in progress'],
                },
              ]}
            />
            <Button disabled={byId.response?.status === status} type="primary" onClick={confirmApply}>
              Apply
            </Button>
            <Button onClick={confirmDelete} icon={<DeleteOutlined />}>
              Delete
            </Button>
          </div>
        )}

        {contextHolder}
      </ContentHeader>

      <Spin spinning={byId.loading} indicator={<LoadingOutlined spin />}>
        <div className={styles.content}>
          <div className={styles.row}>
            <div className={styles.rowTitle}>Ticket No.</div>
            <div className={styles.rowContent}>{byId.response?.ticketId}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowTitle}>Type</div>
            <div className={styles.rowContent}>{byId.response?.type}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowTitle}>Status</div>
            <div className={styles.rowContent}>
              <BadgeTicketStatus status={byId.response?.status} />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowTitle}>Date added</div>
            <div className={styles.rowContent}>{displayDate(byId.response?.createdAt)}</div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowTitle}>Reporter</div>
            <div className={styles.rowContent}>
              {responseUser ? <NavLink to={`/users/${responseUser?.id}`}>{responseUser?.email}</NavLink> : '-'}
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.rowTitle}>Description</div>
            <div className={styles.rowContent}>{byId.response?.description}</div>
          </div>
        </div>
      </Spin>
    </>
  );
}

export default connect(undefined, {
  ticketById: ticketByIdAction,
  ticketByIdClear: ticketByIdClearAction,
  ticketUpdate: ticketUpdateAction,
  ticketUpdateClear: ticketUpdateClearAction,
  ticketUpdateErrorClear: ticketUpdateErrorClearAction,
  ticketDelete: ticketDeleteAction,
  ticketDeleteClear: ticketDeleteClearAction,
})(TicketViewContent);
